import React, { useEffect, useRef, useState } from "react";
import { CircuitContents } from "./CircuitDrawing";
import { pins, pinPosition, nodeSize } from "../engine/registry";
import {
  bounds,
  routeConnections,
  pathFromPoints,
  bendSegment,
  inputTerminal,
} from "../utils/routing";
import { moveComponents, selectionBounds } from "../utils/circuitEditing";
export function canvasPoint(event, svg, view) {
  const matrix = svg.getScreenCTM?.();
  if (matrix && svg.createSVGPoint) {
    const p = svg.createSVGPoint();
    p.x = event.clientX;
    p.y = event.clientY;
    return p.matrixTransform(matrix.inverse());
  }
  const rect = svg.getBoundingClientRect(),
    scale = Math.min(
      (rect.width || 960) / view.w,
      (rect.height || 600) / view.h,
    );
  return {
    x:
      view.x +
      (event.clientX - rect.left - ((rect.width || 960) - view.w * scale) / 2) /
        scale,
    y:
      view.y +
      (event.clientY - rect.top - ((rect.height || 600) - view.h * scale) / 2) /
        scale,
  };
}
export default function CircuitCanvas({
  project,
  selected,
  setSelected,
  selectedWire,
  setSelectedWire,
  onChange,
  onConnect,
  onAdd,
  errors,
  viewToken,
  onDelete,
  onCopy,
  onPaste,
  onUndo,
  onRedo,
  onEdit,
  onEditPin,
  onTerminalEdit,
  onNodeClick,
  armedSource,
  onDisarm,
  onFullscreen,
  fullscreen,
}) {
  const ref = useRef(),
    drag = useRef(null),
    space = useRef(false),
    draft = useRef(null);
  const [view, setView] = useState({ x: -20, y: -20, w: 1000, h: 600 }),
    [preview, setPreview] = useState(null),
    [pending, setPending] = useState(null),
    [cursor, setCursor] = useState(null),
    [snap, setSnap] = useState(true),
    [box, setBox] = useState(null),
    [panMode, setPanMode] = useState(false),
    [isPanning, setIsPanning] = useState(false);
  const cancelDrag = () => {
    drag.current = null;
    draft.current = null;
    setPreview(null);
    setBox(null);
    setIsPanning(false);
  };
  const togglePan = () => {
    cancelDrag();
    setPending(null);
    setCursor(null);
    onDisarm?.();
    setPanMode((active) => !active);
    ref.current?.focus();
  };
  const projectRef = useRef(project);
  projectRef.current = project;
  const point = (e) => canvasPoint(e, ref.current, view),
    snapped = (p) =>
      snap ? { x: Math.round(p.x / 5) * 5, y: Math.round(p.y / 5) * 5 } : p;
  const fit = () => {
    const b = bounds(projectRef.current),
      r = ref.current?.getBoundingClientRect(),
      ratio = r?.width / r?.height || 1.6;
    let w = b.w + 40,
      h = b.h + 40;
    if (w / h < ratio) w = h * ratio;
    else h = w / ratio;
    setView({ x: b.x - (w - b.w) / 2, y: b.y - (h - b.h) / 2, w, h });
  };
  useEffect(() => {
    fit();
  }, [viewToken]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!ref.current || typeof ResizeObserver === "undefined") return;
    let first = true;
    const observer = new ResizeObserver(() => {
      if (first) {
        fit();
        first = false;
      }
    });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (armedSource) {
      setPanMode(false);
      setPending(armedSource);
      ref.current?.focus();
    }
  }, [armedSource]);
  useEffect(() => {
    const key = (e) => {
        if (
          e.target.closest?.(
            'input,textarea,select,[contenteditable],dialog,[role="dialog"]',
          )
        )
          return;
        if (e.code === "Space" && !e.target.closest?.("button,a")) {
          space.current = true;
          e.preventDefault();
        }
        if (e.key === "Escape") {
          cancelDrag();
          setPanMode(false);
          setPending(null);
          onDisarm?.();
          setSelected([]);
          setSelectedWire(null);
        }
        if (e.key === "Delete" || e.key === "Backspace") {
          e.preventDefault();
          onDelete();
        }
        const k = e.key.toLowerCase();
        if (e.ctrlKey || e.metaKey) {
          if (["a", "c", "v", "d", "z", "y"].includes(k)) e.preventDefault();
          if (k === "a") {
            setSelected(project.components.map((c) => c.id));
            setSelectedWire(null);
          }
          if (k === "c") onCopy();
          if (k === "v") onPaste();
          if (k === "d") onPaste(true);
          if (k === "y" || (k === "z" && e.shiftKey)) onRedo();
          else if (k === "z") onUndo();
        } else if (!e.altKey && k === "h" && !e.repeat) {
          e.preventDefault();
          togglePan();
        } else if (k === "f") fit();
      },
      up = (e) => {
        if (e.code === "Space") space.current = false;
      };
    const blur = () => {
      space.current = false;
      cancelDrag();
    };
    window.addEventListener("blur", blur);
    window.addEventListener("keydown", key);
    window.addEventListener("keyup", up);
    return () => {
      window.removeEventListener("blur", blur);
      window.removeEventListener("keydown", key);
      window.removeEventListener("keyup", up);
    };
  });
  const capture = (e) => {
    ref.current.focus();
    ref.current.setPointerCapture?.(e.pointerId);
  };
  const show = (p) => {
    draft.current = p;
    setPreview(p);
  };
  const complete = (source, target) => {
    if (!source) return;
    onConnect(source, target);
    setPending(null);
    onDisarm?.();
  };
  const nodeDown = (e, id) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    capture(e);
    setSelectedWire(null);
    const ids = e.shiftKey
      ? selected.includes(id)
        ? selected.filter((s) => s !== id)
        : [...selected, id]
      : selected.includes(id)
        ? selected
        : [id];
    setSelected(ids);
    drag.current = { kind: "nodes", start: point(e), base: project, ids };
  };
  const pinDown = (e, c, p, direction) => {
    e.preventDefault();
    capture(e);
    if (direction === "out") {
      const from = { component: c, pin: p };
      setPending(from);
      drag.current = { kind: "wire", from };
    } else if (pending) complete(pending, { component: c, pin: p });
    else {
      const edge = project.connections.find(
        (w) => w.to.component === c && w.to.pin === p,
      );
      if (edge) {
        setPending(edge.from);
        setSelectedWire(edge.id);
        drag.current = { kind: "wire", from: edge.from };
      }
    }
  };
  const finish = (e) => {
    const d = drag.current;
    drag.current = null;
    setIsPanning(false);
    if (draft.current) {
      onChange(draft.current);
      draft.current = null;
      setPreview(null);
    }
    if (d?.kind === "select" && box) {
      const x = Math.min(box.a.x, box.b.x),
        y = Math.min(box.a.y, box.b.y),
        r = Math.max(box.a.x, box.b.x),
        b = Math.max(box.a.y, box.b.y);
      const inside = project.components
        .filter((c) => {
          const z = nodeSize(c);
          return c.x + z.w >= x && c.x <= r && c.y + z.h >= y && c.y <= b;
        })
        .map((c) => c.id);
      setSelected([...new Set([...(d.add ? d.initial : []), ...inside])]);
      setBox(null);
    }
    if (d?.kind === "wire") {
      const pos = point(e);
      for (const c of project.components)
        for (const p of pins(c).inputs) {
          const target = pinPosition(c, p.id, "in");
          if (Math.hypot(pos.x - target.x, pos.y - target.y) < 11) {
            complete(d.from, { component: c.id, pin: p.id });
            return;
          }
        }
    }
    if(d?.kind==='nodes'&&d.ids.length===1){const pos=point(e);if(Math.hypot(pos.x-d.start.x,pos.y-d.start.y)<3)onNodeClick?.(d.ids[0]);}
    ref.current.releasePointerCapture?.(e.pointerId);
  };
  const visual = preview || project,
    group = selectionBounds(visual, selected);
  const chosen = selectedWire
    ? routeConnections(visual).find((r) => r.e.id === selectedWire)
    : null;
  const zoom = (factor, anchor) =>
    setView((v) => {
      const a = anchor || { x: v.x + v.w / 2, y: v.y + v.h / 2 },
        w = Math.max(220, Math.min(12000, v.w * factor)),
        r = w / v.w;
      return {
        x: a.x - (a.x - v.x) * r,
        y: a.y - (a.y - v.y) * r,
        w,
        h: v.h * r,
      };
    });
  return (
    <div className="canvas-shell compact-canvas">
      <div className="canvas-context">
        <span>
          {pending
            ? "Click an input pin to connect"
            : `${project.components.length} components · ${project.connections.length} wires`}
        </span>
        <span className="canvas-hint">
          {panMode
            ? "Pan mode · drag anywhere to move the view · Esc to exit"
            : "Drag to move · double-click to edit · drag a wire handle to bend"}
        </span>
      </div>
      <svg
        ref={ref}
        className={`circuit-canvas${panMode ? " is-pan-mode" : ""}${isPanning ? " is-panning" : ""}`}
        tabIndex="0"
        aria-label="Editable circuit canvas"
        viewBox={`${view.x} ${view.y} ${view.w} ${view.h}`}
        preserveAspectRatio="xMidYMid meet"
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const type = e.dataTransfer.getData("application/component");
          if (type) onAdd(type, snapped(point(e)));
        }}
        onDoubleClickCapture={(e) => {
          if (panMode || space.current) {
            e.preventDefault();
            e.stopPropagation();
          }
        }}
        onPointerDownCapture={(e) => {
          if (e.button !== 0 && e.button !== 1) return;
          if (!panMode && !space.current && e.button !== 1) return;
          e.preventDefault();
          e.stopPropagation();
          capture(e);
          drag.current = {
            kind: "pan",
            start: { x: e.clientX, y: e.clientY },
            view,
          };
          setIsPanning(true);
        }}
        onPointerDown={(e) => {
          if (e.button !== 0) return;
          e.preventDefault();
          capture(e);
          setSelectedWire(null);
          if (!e.shiftKey) setSelected([]);
          drag.current = {
            kind: "select",
            start: point(e),
            initial: selected,
            add: e.shiftKey,
          };
          setBox({ a: point(e), b: point(e) });
        }}
        onPointerMove={(e) => {
          const pos = point(e),
            p = snapped(pos);
          if (pending) setCursor(pos);
          const d = drag.current;
          if (!d) return;
          if (d.kind === "nodes") {
            const delta = {
              x: p.x - snapped(d.start).x,
              y: p.y - snapped(d.start).y,
            };
            show(
              moveComponents(
                d.base,
                d.base.components
                  .filter((c) => d.ids.includes(c.id))
                  .map((c) => ({
                    id: c.id,
                    x: c.x + delta.x,
                    y: c.y + delta.y,
                  })),
              ),
            );
          }
          if (d.kind === "resize") {
            const next = JSON.parse(JSON.stringify(d.base)),
              c = next.components.find((c) => c.id === d.id);
            let w = Math.max(24, d.size.w + p.x - d.start.x),
              h = Math.max(24, d.size.h + p.y - d.start.y);
            if (e.shiftKey) h = (w * d.size.h) / d.size.w;
            c.size = { w: Math.min(800, w), h: Math.min(1200, h) };
            show(next);
          }
          if (d.kind === "bend") {
            const next = JSON.parse(JSON.stringify(d.base)),
              wire = next.connections.find((w) => w.id === d.id);
            wire.waypoints = bendSegment(d.points, d.index, p).slice(1, -1);
            wire.anchors = {
              start: d.points[0],
              end: d.points[d.points.length - 1],
            };
            delete wire.routeOffset;
            show(next);
          }
          if (d.kind === "pan") {
            const r = ref.current.getBoundingClientRect(),
              scale = Math.min((r.width || 960) / d.view.w, (r.height || 600) / d.view.h);
            setView({
              ...d.view,
              x: d.view.x - (e.clientX - d.start.x) / scale,
              y: d.view.y - (e.clientY - d.start.y) / scale,
            });
          }
          if (d.kind === "select") setBox({ a: d.start, b: pos });
        }}
        onPointerUp={finish}
        onPointerCancel={cancelDrag}
        onLostPointerCapture={() => {
          if (drag.current?.kind === "pan") cancelDrag();
        }}
        onWheel={(e) => {
          if (e.ctrlKey || e.metaKey) e.preventDefault();
          zoom(e.deltaY > 0 ? 1.08 : 1 / 1.08, point(e));
        }}
      >
        <defs>
          <pattern
            id="studio-grid"
            width="10"
            height="10"
            patternUnits="userSpaceOnUse"
          >
            <circle cx="0" cy="0" r=".55" fill="#dbe2dc" />
          </pattern>
        </defs>
        <rect
          x={view.x}
          y={view.y}
          width={view.w}
          height={view.h}
          fill="#f4f6f3"
        />
        <rect
          x="0"
          y="0"
          width="960"
          height={Math.max(420, bounds(visual).h)}
          fill="white"
          stroke="#d9e1dc"
          strokeDasharray="5 4"
        />
        <rect
          x={view.x}
          y={view.y}
          width={view.w}
          height={view.h}
          fill="url(#studio-grid)"
        />
        <text x="12" y="18" fontSize="9" fill="#829088" pointerEvents="none">
          A4 printable width
        </text>
        <CircuitContents
          project={visual}
          selected={selected}
          selectedWire={selectedWire}
          errorIds={errors}
          onNodeDown={nodeDown}
          onPinDown={pinDown}
          onEdit={onEdit}
          onEditPin={onEditPin}
          onTerminalEdit={id => {setPending(null); onDisarm?.(); onTerminalEdit?.(id);}}
          onTerminalDown={(e, from) => {if(e.button!==0)return; e.preventDefault(); capture(e); setPending(from); setCursor(point(e)); drag.current={kind:"wire",from};}}
          onWireDown={(e, id) => {
            ref.current.focus();
            setSelectedWire(id);
            setSelected([]);
          }}
          onBranch={setPending}
        />
        {group && selected.length > 1 && (
          <rect
            {...{ x: group.x, y: group.y, width: group.w, height: group.h }}
            fill="none"
            stroke="#417e72"
            strokeWidth="1"
            strokeDasharray="5 4"
            pointerEvents="none"
          />
        )}
        {selected.map((id) => {
          const c = visual.components.find((c) => c.id === id);
          if (!c) return null;
          const z = nodeSize(c);
          return (
            <rect
              key={id}
              x={c.x + z.w - 4}
              y={c.y + z.h - 4}
              width="8"
              height="8"
              fill="white"
              stroke="#367a6a"
              data-testid={`resize-${id}`}
              aria-label={`Resize ${c.name}`}
              style={{ cursor: "nwse-resize" }}
              onPointerDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                capture(e);
                drag.current = {
                  kind: "resize",
                  id,
                  base: project,
                  start: snapped(point(e)),
                  size: z,
                };
              }}
            />
          );
        })}
        {chosen &&
          chosen.points.slice(0, -1).map((a, i) => {
            const b = chosen.points[i + 1];
            if (Math.hypot(a.x - b.x, a.y - b.y) < 8) return null;
            return (
              <g key={i}>
                <circle
                  cx={(a.x + b.x) / 2}
                  cy={(a.y + b.y) / 2}
                  r="7"
                  fill="white"
                  stroke="#367a6a"
                  strokeWidth="1.5"
                  data-testid={`wire-handle-${i}`}
                  aria-label={`Bend wire segment ${i + 1}`}
                  style={{ cursor: a.y === b.y ? "ns-resize" : "ew-resize" }}
                  onPointerDown={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    capture(e);
                    drag.current = {
                      kind: "bend",
                      id: chosen.e.id,
                      base: project,
                      points: chosen.points,
                      index: i,
                    };
                  }}
                />
              </g>
            );
          })}
        {pending &&
          cursor &&
          (() => {
            const c = visual.components.find((c) => c.id === pending.component),
              start = c
                ? pinPosition(c, pending.pin, "out")
                : (pending.input && inputTerminal(visual, pending.input)) || { x: cursor.x - 45, y: cursor.y };
            return (
              <path
                d={pathFromPoints([
                  start,
                  { x: (start.x + cursor.x) / 2, y: start.y },
                  { x: (start.x + cursor.x) / 2, y: cursor.y },
                  cursor,
                ])}
                stroke="#367a6a"
                fill="none"
                strokeDasharray="4 3"
                strokeWidth="1.5"
                pointerEvents="none"
              />
            );
          })()}
        {box && (
          <rect
            x={Math.min(box.a.x, box.b.x)}
            y={Math.min(box.a.y, box.b.y)}
            width={Math.abs(box.a.x - box.b.x)}
            height={Math.abs(box.a.y - box.b.y)}
            fill="#367a6a15"
            stroke="#367a6a"
            pointerEvents="none"
          />
        )}
        {!project.components.length && (
          <text
            x="480"
            y="180"
            textAnchor="middle"
            fontSize="18"
            fill="#89958e"
            pointerEvents="none"
          >
            Add components or generate a circuit
          </text>
        )}
      </svg>
      <div className="canvas-controls">
        <button onClick={() => zoom(1.2)} aria-label="Zoom out">
          −
        </button>
        <button onClick={() => zoom(0.8)} aria-label="Zoom in">
          +
        </button>
        <button onClick={fit}>Fit</button>
        <button
          onClick={() => {
            setSelected(project.components.map((c) => c.id));
            setSelectedWire(null);
            ref.current.focus();
          }}
        >
          Select all
        </button>
        <label className="check">
          <input
            type="checkbox"
            checked={snap}
            onChange={(e) => setSnap(e.target.checked)}
          />
          Snap
        </label>
        <button
          className="canvas-pan"
          aria-label="Pan"
          aria-pressed={panMode}
          title="Pan · H"
          onClick={togglePan}
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M8 13V5a1.5 1.5 0 0 1 3 0v6-8a1.5 1.5 0 0 1 3 0v8-6a1.5 1.5 0 0 1 3 0v7-4a1.5 1.5 0 0 1 3 0v7c0 4-2.5 7-6 7h-1c-2 0-3.5-1-4.5-2.5L4 13a1.6 1.6 0 0 1 2.5-2Z" />
          </svg>
          Pan
        </button>
        <button onClick={onFullscreen}>
          {fullscreen ? "Exit full screen" : "Full screen"}
        </button>
      </div>
    </div>
  );
}
